<template>
	<aside class="chat-drawer" role="dialog" :aria-label="$t('chat.open_chats_title')">
		<header class="chat-drawer__header">
			<div class="chat-drawer__heading">
				<h2 class="chat-drawer__title">{{ $t('chat.open_chats_title') }}</h2>
				<div v-if="ready" class="chat-drawer__counters">
					<span class="chat-drawer__counter">
						<b>{{ chats.length }}</b>
						{{ $t('chat.counter_open') }}
					</span>
					<span class="chat-drawer__counter" :class="{ 'chat-drawer__counter--hot': unread > 0 }">
						<b>{{ unread }}</b>
						{{ $t('chat.counter_unread') }}
					</span>
				</div>
			</div>
			<button type="button" class="chat-drawer__close" :aria-label="$t('chat.close')" @click="$emit('close')">
				<chat-icon name="close" :size="22" />
			</button>
		</header>

		<template v-if="ready">
			<div class="chat-drawer__support">
				<div v-if="confirmingSupport" class="chat-drawer__confirm" role="alertdialog">
					<p>{{ $t('chat.start_confirm') }}</p>
					<div class="chat-drawer__confirm-actions">
						<button type="button" class="chat-drawer__ghost" @click="confirmingSupport = false">
							{{ $t('chat.cancel') }}
						</button>
						<button type="button" class="chat-drawer__primary" @click="startSupportChat">
							{{ $t('chat.start_action') }}
						</button>
					</div>
				</div>
				<button v-else type="button" class="chat-drawer__support-button" @click="chatWithSupport">
					<span class="chat-drawer__support-icon">
						<chat-icon name="support" :size="20" />
					</span>
					<span class="chat-drawer__support-label">{{ $t('chat.chat_with_support') }}</span>
					<span v-if="supportUnread > 0" class="chat-drawer__support-badge">{{ supportUnreadLabel }}</span>
					<span v-else-if="supportChat" class="chat-drawer__support-continue">
						{{ $t('chat.channel_continue') }}
					</span>
					<chat-icon v-else name="chevron-right" :size="20" />
				</button>
			</div>

			<div class="chat-drawer__filters">
				<label class="chat-drawer__search">
					<chat-icon name="search" :size="16" />
					<input
						ref="search"
						v-model="search"
						type="search"
						spellcheck="false"
						:placeholder="$t('chat.search_placeholder')"
						:aria-label="$t('chat.search_placeholder')"
					/>
				</label>
				<div class="chat-drawer__chips">
					<button
						v-for="chip in chips"
						:key="chip.id"
						type="button"
						class="chat-chip"
						:class="{ 'chat-chip--on': filter === chip.id }"
						:aria-pressed="filter === chip.id ? 'true' : 'false'"
						@click="filter = chip.id"
					>
						{{ chip.label }}
						<i>{{ chip.count }}</i>
					</button>
				</div>
			</div>

			<div class="chat-drawer__body">
				<div v-if="loading && !chats.length" class="chat-drawer__state">
					<app-icon icon="spin-loading" class="chat-drawer__spinner" />
					<span>{{ $t('chat.loading') }}</span>
				</div>
				<div v-else-if="loadFailed && !chats.length" class="chat-drawer__state chat-drawer__state--error">
					<chat-icon name="alert" :size="32" />
					<span>{{ $t('chat.load_failed') }}</span>
				</div>
				<div v-else-if="!visibleChats.length" class="chat-drawer__state">
					<chat-icon name="chat-outline" :size="32" class="chat-drawer__state-icon" />
					<span>{{ chats.length ? $t('chat.no_matches') : $t('chat.no_open_chats') }}</span>
				</div>
				<ul v-else class="chat-drawer__list">
					<chat-row
						v-for="(chat, index) in visibleChats"
						:key="`${chat.typeId}:${chat.chatId}`"
						:chat="chat"
						:index="index"
						:show-business="severalRestaurants"
						:restaurants="user ? user.restaurants : []"
						@open="openRow"
					/>
				</ul>
			</div>
		</template>

		<div v-else-if="phase === 'needsLogin'" class="chat-drawer__state chat-drawer__login">
			<chat-icon name="login" :size="36" class="chat-drawer__state-icon" />
			<span>{{ $t('chat.needs_login_title') }}</span>
			<small>{{ $t('chat.needs_login_text') }}</small>
			<button type="button" class="chat-drawer__primary" :disabled="loggingOut" @click="logInAgain">
				<app-icon v-if="loggingOut" icon="spin-loading" />
				{{ $t('chat.log_in_again') }}
			</button>
		</div>

		<div v-else class="chat-drawer__state">
			<app-icon icon="spin-loading" class="chat-drawer__spinner" />
			<span>{{ $t('chat.connecting') }}</span>
		</div>
	</aside>
</template>

<script>
	import { mapGetters } from 'vuex';
	import AuthService from '@/services/AuthService';
	import UtilityService from '@/services/UtilityService';
	import RouteNames from '@/router/names';
	import {
		CHAT_TYPE,
		GENERAL_ORDER_CODE,
		chatChannelLabelKey,
		chatDisplayName,
		unreadBadgeLabel,
	} from '@/services/chat/channels';
	import {
		chatPhase,
		chatStore,
		generalSupportChat,
		hasSeveralRestaurants,
		openChat,
		toggleDrawer,
		totalUnread,
	} from '@/services/chat/chatStore';
	import ChatIcon from './ChatIcon.vue';
	import ChatRow from './ChatRow.vue';
	import { showChatToast } from './chatToasts';

	const FILTERS = [
		{ id: 'all', typeId: null, labelKey: 'chat.filter_all' },
		{ id: 'support', typeId: CHAT_TYPE.SUPPORT, labelKey: 'chat.filter_support' },
		{ id: 'customers', typeId: CHAT_TYPE.CUSTOMER, labelKey: 'chat.filter_customers' },
		{ id: 'drivers', typeId: CHAT_TYPE.DRIVER, labelKey: 'chat.filter_drivers' },
	];

	// Case and accents apart, so "e" finds "ë" and "c" finds "ç".
	const normalize = (value) =>
		String(value || '')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.trim();

	export default {
		name: 'ChatDrawer',
		components: { ChatIcon, ChatRow },
		props: {
			loading: {
				type: Boolean,
				default: false,
			},
			loadFailed: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['close'],
		data: () => ({
			search: '',
			filter: 'all',
			confirmingSupport: false,
			loggingOut: false,
		}),
		computed: {
			...mapGetters({
				user: 'user',
			}),
			phase() {
				return chatPhase();
			},
			ready() {
				return this.phase === 'ready';
			},
			chats() {
				return chatStore.openChats;
			},
			unread() {
				return totalUnread();
			},
			severalRestaurants() {
				return hasSeveralRestaurants(this.user);
			},
			supportChat() {
				return generalSupportChat();
			},
			supportUnread() {
				return this.supportChat ? this.supportChat.unreadCount : 0;
			},
			supportUnreadLabel() {
				return unreadBadgeLabel(this.supportUnread);
			},
			chips() {
				return FILTERS.map((filter) => ({
					id: filter.id,
					label: this.$t(filter.labelKey),
					count: filter.typeId
						? this.chats.filter((chat) => chat.typeId === filter.typeId).length
						: this.chats.length,
				}));
			},
			visibleChats() {
				const typeId = FILTERS.find((filter) => filter.id === this.filter).typeId;
				const needle = normalize(this.search);
				return this.chats.filter(
					(chat) =>
						(!typeId || chat.typeId === typeId) && (!needle || this.searchTextOf(chat).includes(needle))
				);
			},
		},
		watch: {
			ready(ready) {
				if (!ready) {
					this.confirmingSupport = false;
				}
			},
		},
		methods: {
			searchTextOf(chat) {
				return normalize(
					[
						chatDisplayName(this.$t, chat.typeId, chat.counterpart),
						this.$t(chatChannelLabelKey(chat.typeId)),
						chat.orderCode === GENERAL_ORDER_CODE ? '' : chat.orderCode,
						this.severalRestaurants ? chat.businessName : '',
						chat.lastMessage ? chat.lastMessage.text : '',
					].join(' ')
				);
			},
			open(target) {
				if (!openChat(target)) {
					showChatToast({ kind: 'error', text: this.$t('chat.unavailable') });
					return;
				}
				// On a phone the drawer covers the whole screen, the chat window included.
				if (UtilityService.isMobile()) {
					toggleDrawer(false);
				}
			},
			openRow(chat) {
				this.open({ typeId: chat.typeId, chatId: chat.chatId });
			},
			chatWithSupport() {
				if (this.supportChat) {
					this.open({ typeId: this.supportChat.typeId, chatId: this.supportChat.chatId });
				} else {
					this.confirmingSupport = true;
				}
			},
			startSupportChat() {
				this.confirmingSupport = false;
				this.open({ typeId: CHAT_TYPE.SUPPORT, orderCode: GENERAL_ORDER_CODE });
			},
			// Content.vue's logout, then the login page: only a fresh login brings a chat token.
			logInAgain() {
				const { user } = this;
				this.loggingOut = true;
				const unregister = user ? AuthService.deleteRegId(user.id, user.regId) : Promise.resolve();
				unregister
					.catch(() => null)
					.then(() => {
						this.$store.dispatch('logout');
						this.$router.push({ name: RouteNames.Login });
					});
			},
			// Escape in a non-empty search clears it (the browser does too) instead of closing the drawer.
			onKeydown(event) {
				if (event.key !== 'Escape' || event.defaultPrevented) {
					return;
				}
				if (this.search && event.target === this.$refs.search) {
					this.search = '';
					return;
				}
				this.$emit('close');
			},
		},
		mounted() {
			document.addEventListener('keydown', this.onKeydown);
		},
		beforeUnmount() {
			document.removeEventListener('keydown', this.onKeydown);
		},
	};
</script>

<style lang="scss" scoped>
	$chat-green: #08cc04;
	$ink: #1a202c;
	$text: #4a5568;
	$muted: #718096;
	$line: #e2e8f0;
	$surface: #f7fafc;

	.chat-drawer {
		position: fixed;
		top: 0;
		right: 0;
		z-index: 10002;
		display: flex;
		flex-direction: column;
		width: 384px;
		max-width: 100vw;
		height: 100%;
		color: $ink;
		background: $surface;
		box-shadow: -18px 0 48px rgba(15, 23, 42, 0.18);
	}

	.chat-drawer__header {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 14px 12px 14px 20px;
		overflow: hidden;
		color: var(--white);
		background: $chat-green;

		&::before {
			content: '';
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			background: repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.035) 0 1px, transparent 1px 12px),
				radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.12), transparent 55%);
			pointer-events: none;
		}
	}

	.chat-drawer__heading {
		position: relative;
		display: flex;
		flex: 1;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		min-width: 0;
	}

	.chat-drawer__title {
		font-size: 22px;
		font-weight: 800;
		line-height: 1.2;
	}

	.chat-drawer__counters {
		display: flex;
		flex-shrink: 0;
		align-items: flex-end;
		gap: 12px;
	}

	.chat-drawer__counter {
		display: flex;
		flex-direction: column;
		align-items: center;
		font-size: 10px;
		font-weight: 700;
		line-height: 1;

		b {
			display: flex;
			align-items: center;
			justify-content: center;
			min-width: 30px;
			height: 30px;
			margin-bottom: 4px;
			font-size: 20px;
			font-weight: 800;
			font-variant-numeric: tabular-nums;
		}
	}

	.chat-drawer__counter--hot b {
		padding: 0 7px;
		color: $chat-green;
		background: var(--white);
		border-radius: 9999px;
	}

	.chat-drawer__close {
		position: relative;
		display: flex;
		padding: 4px;
		color: var(--white);
		background: transparent;
		border: 0;
		border-radius: 9999px;
		cursor: pointer;

		&:hover {
			background: rgba(255, 255, 255, 0.18);
		}
	}

	.chat-drawer__support {
		padding: 12px 12px 0;
		background: var(--white);
	}

	.chat-drawer__support-button {
		display: flex;
		align-items: center;
		gap: 10px;
		width: 100%;
		padding: 8px 10px;
		font-size: 13px;
		font-weight: 700;
		color: $ink;
		text-align: left;
		background: #cdf4d8;
		border: 1px solid rgba(14, 183, 70, 0.3);
		border-radius: 10px;
		cursor: pointer;
		transition: border-color 0.15s ease, background-color 0.15s ease;

		&:hover {
			border-color: var(--success);
		}
	}

	.chat-drawer__support-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		color: var(--white);
		background: var(--success);
		border-radius: 9999px;
	}

	.chat-drawer__support-label {
		flex: 1;
		min-width: 0;
	}

	.chat-drawer__support-badge {
		min-width: 22px;
		height: 22px;
		padding: 0 6px;
		font-size: 11px;
		font-weight: 800;
		line-height: 22px;
		text-align: center;
		color: var(--white);
		background: var(--primary);
		border-radius: 9999px;
		font-variant-numeric: tabular-nums;
	}

	.chat-drawer__support-continue {
		font-size: 12px;
		color: var(--success);
	}

	.chat-drawer__confirm {
		padding: 10px 12px;
		font-size: 13px;
		font-weight: 600;
		line-height: 1.35;
		background: #cdf4d8;
		border: 1px solid rgba(14, 183, 70, 0.3);
		border-radius: 10px;
	}

	.chat-drawer__confirm-actions {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 8px;
	}

	.chat-drawer__primary,
	.chat-drawer__ghost {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 14px;
		font-size: 13px;
		font-weight: 700;
		border-radius: 9999px;
		cursor: pointer;
	}

	.chat-drawer__primary {
		color: var(--white);
		background: var(--success);
		border: 1px solid var(--success);
	}

	.chat-drawer__ghost {
		color: $text;
		background: var(--white);
		border: 1px solid $line;
	}

	.chat-drawer__filters {
		padding: 12px 12px 10px;
		background: var(--white);
		border-bottom: 1px solid $line;
	}

	.chat-drawer__search {
		display: flex;
		align-items: center;
		gap: 8px;
		height: 36px;
		padding: 0 10px;
		color: $muted;
		background: $surface;
		border: 1px solid $line;
		border-radius: 8px;
		transition: border-color 0.15s ease, background-color 0.15s ease;

		&:focus-within {
			background: var(--white);
			border-color: var(--primary);
		}

		input {
			flex: 1;
			min-width: 0;
			font-size: 12px;
			color: $ink;
			background: transparent;
			border: 0;
			outline: none;

			&::placeholder {
				color: $muted;
			}
		}
	}

	.chat-drawer__chips {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px;
		margin-top: 10px;
	}

	.chat-chip {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		height: 24px;
		padding: 0 8px;
		font-size: 11px;
		font-weight: 600;
		color: $text;
		background: var(--white);
		border: 1px solid $line;
		border-radius: 9999px;
		cursor: pointer;
		transition: all 0.15s ease;

		&:hover {
			border-color: $chat-green;
		}

		i {
			font-style: normal;
			font-weight: 700;
			color: $muted;
			font-variant-numeric: tabular-nums;
		}
	}

	.chat-chip--on {
		color: var(--white);
		background: $chat-green;
		border-color: $chat-green;

		i {
			color: var(--white);
			opacity: 0.75;
		}
	}

	.chat-drawer__body {
		flex: 1;
		min-height: 0;
		overflow-y: auto;

		&::-webkit-scrollbar {
			width: 4px;
		}

		&::-webkit-scrollbar-thumb {
			background: #cbd5e0;
			border-radius: 6px;
		}
	}

	.chat-drawer__list {
		padding: 4px 0 12px;
	}

	.chat-drawer__state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 48px 24px;
		font-size: 13px;
		font-weight: 600;
		color: $text;
		text-align: center;

		small {
			font-size: 12px;
			font-weight: 400;
			line-height: 1.5;
			color: $muted;
		}
	}

	.chat-drawer__state--error {
		color: var(--error);
	}

	.chat-drawer__state-icon {
		color: #a0aec0;
	}

	.chat-drawer__spinner {
		font-size: 28px;
		color: $chat-green;
	}

	.chat-drawer__login .chat-drawer__primary {
		margin-top: 8px;
	}
</style>
